import { enqueueSnackbar } from 'notistack';
import { Tooltip, Grid, Box } from '@mui/material';
import {
  downloadReportResults,
  reportTimeout,
  updateReportResults,
  updateToMuchData,
} from '../../../redux/slices/reports';
import {
  PURCHASING_ITEMS_ORDERED,
  TOP_LEVEL_BUDGET_REPORT,
  FACILITY_HISTORY_REPORT,
  FACILITY_COMPARISON_REPORT,
  VENDOR_COMPARISON_REPORT,
  PO_INVOICE_REPORT,
  SUBSTITUTE_REPORT,
  FACILITIES_INVOICES_REPORT,
  INVOICES_ISSUES_REPORT,
  FACILITIES_INVOICES_DISCREPANCY,
  ITEMS_ORDERED_FULL,
  USERS_COLUMNS,
  PO_INVOICE_CONSOLIDATED_REPORT,
  CENSUS_INTEGRATION_REPORT,
  PRICE_INCREASE_REPORT,
  SERIAL_NUMBER_MATCHUP_REPORT,
  CENSUS_REPORT,
  BID_SHEET_REPORT,
  REJECTED_ITEMS_BY_VENDOR_REPORT,
  BUDGETS_REPORT,
  TRANSPORT_REQUESTS_REPORT,
  TRANSPORT_REQUESTS_DEFAULT_HIDDEN_COLUMNS,
  TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT,
} from '../../../reusable-components/datagrid/reportsColumns';
import { fMonthDayYear } from '../../../utils/formatTime';
import { fCurrency } from '../../../utils/formatNumber';
import { TRANSPORT_REQUEST_REPORT_ID, TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID } from './Constants';

export function getColumnsMain(selectedReportId) {
  switch (selectedReportId) {
    case 1:
      return { columns: PURCHASING_ITEMS_ORDERED };
    case 2:
      return {
        columns: FACILITY_COMPARISON_REPORT,
        dynamicColumns: 'facilities',
        dynamicKey: ['facility'],
        dynamicFields: [
          { field: 'ppdPrice', headerName: 'PPD', isCurrency: true },
          { field: 'quantity', headerName: 'QTY', isNumber: true },
          { field: 'total', headerName: 'Total', isCurrency: true, tooltip: 'Price * Quantity' },
        ],
      };
    case 3:
      return {
        columns: VENDOR_COMPARISON_REPORT,
        nestedDataArray: true,
        dynamicColumns: 'vendors',
        dynamicKey: ['vendor'],
        dynamicFields: [
          { field: 'manufacturer', headerName: 'Manufacturer' },
          { field: 'uom', headerName: 'UOM' },
          { field: 'baseUom', headerName: 'Base UOM', combineTwoFields: ['baseUomAmount', 'baseUomType'] },
          { field: 'perItem', headerName: 'Per Item', isCurrency: true },
          { field: 'perUnit', headerName: 'Per Unit', isCurrency: true },
          { field: 'price', headerName: 'Price', isCurrency: true },
          { field: 'vendorItemId', headerName: 'SKU' },
        ],
      };
    case 4:
      return { columns: TOP_LEVEL_BUDGET_REPORT };
    case 5:
      return {
        columns: FACILITY_HISTORY_REPORT,
        nestedData: 'items',
        nestedDataKey: 'item',
        dynamicColumns: 'orderedItems',
        topLevelColumns: ['censusDateValues'],
        topLevelColumnsKey: 'date',
        dynamicKey: ['date'],
        dynamicFields: [
          { field: 'total', headerName: 'Total', isCurrency: true },
          { field: 'quantity', headerName: 'QTY', isNumber: true },
          { field: 'ppdTotal', headerName: 'PPD Total', isCurrency: true },
        ],
      };
    case 6:
      return { columns: PO_INVOICE_REPORT };
    case 7:
      return { columns: SUBSTITUTE_REPORT };
    case 8:
      return { columns: FACILITIES_INVOICES_REPORT };
    case 9:
      return { columns: INVOICES_ISSUES_REPORT };
    case 10:
      return {
        columns: FACILITIES_INVOICES_DISCREPANCY,
        getRowId: (row) =>
          `${row.id}_${row.account}_${row.facility}_${row.item}_${row.vendor}_${row.vendorInvoiceId}_${row.sent}_${row.invoiceDate}_${row.issue}`,
      };
    case 11:
      return { columns: ITEMS_ORDERED_FULL };
    case 13:
      return { columns: USERS_COLUMNS };
    case 14:
      return { columns: PO_INVOICE_CONSOLIDATED_REPORT };
    case 18:
      return { columns: CENSUS_INTEGRATION_REPORT };
    case 23:
      return {
        columns: SERIAL_NUMBER_MATCHUP_REPORT,
        getRowId: (row) => `${row.serialNumber}_${row.PersonFor}_${row.description}_${row.facility}_${row.marker}`,
      };
    case 21:
      return { columns: PRICE_INCREASE_REPORT };
    case 24:
      return { columns: CENSUS_REPORT };
    case TRANSPORT_REQUEST_REPORT_ID:
      return {
        columns: TRANSPORT_REQUESTS_REPORT,
        defaultHiddenColumns: TRANSPORT_REQUESTS_DEFAULT_HIDDEN_COLUMNS,
        getRowId: (row) => `${row.id}_${row.appointmentDateTime}_${row.pickupTime}_${row.facility}_${row.direction}`,
      };
    case 26:
      return { columns: BID_SHEET_REPORT };
    case 27:
      return { columns: REJECTED_ITEMS_BY_VENDOR_REPORT };
    case 28:
      return { columns: BUDGETS_REPORT };
    case TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID:
      return { columns: TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT };
    default:
      return { columns: PURCHASING_ITEMS_ORDERED };
  }
}

export function getAllColumnsMain(selectedReportId, reportColumns, dynamicColumns) {
  if (!reportColumns?.dynamicColumns || !dynamicColumns) return reportColumns?.columns || [];
  return [
    ...reportColumns?.columns,
    ...dynamicColumns.map((col) => ({
      field: col,
      headerName: col,
      width: selectedReportId === 3 ? 500 : 250,
      alignItems: 'center',
      renderCell: (params) => {
        const nameLookup =
          reportColumns?.topLevelColumnsKey === 'date'
            ? params?.row[reportColumns?.dynamicColumns]?.find(
                (item) => fMonthDayYear(item[reportColumns?.dynamicKey]) === col
              )
            : params?.row[reportColumns?.dynamicColumns]?.filter((item) => item[reportColumns?.dynamicKey] === col);

        return (
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
            {reportColumns?.dynamicFields &&
              reportColumns?.dynamicFields.map((fieldObj) => {
                const { combineTwoFields, isCurrency, headerName, field, isNumber, tooltip } = fieldObj;
                return (
                  <Tooltip key={field} title={tooltip} arrow placement="top">
                    <Grid item key={field}>
                      <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start">
                        <Grid item>
                          <Box
                            component="div"
                            sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                          >
                            {headerName}
                          </Box>
                        </Grid>
                        {nameLookup?.length > 0 ? (
                          nameLookup.map((item, index) => (
                            <Grid item key={index}>
                              <Box
                                component="div"
                                sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                              >
                                {combineTwoFields
                                  ? `${item?.[combineTwoFields[0]] || 0} / ${item?.[combineTwoFields[1]] || 0}`
                                  : isCurrency
                                  ? fCurrency(item?.[field])
                                  : isNumber
                                  ? item?.[field] || 0
                                  : item?.[field] || ''}
                              </Box>
                            </Grid>
                          ))
                        ) : (
                          <Box
                            component="div"
                            sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                          >
                            {isCurrency
                              ? fCurrency(nameLookup?.[field])
                              : isNumber
                              ? nameLookup?.[field] || 0
                              : nameLookup?.[field] || ''}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Tooltip>
                );
              })}
          </Grid>
        );
      },
      valueGetter: (params) => {
        const nameLookup =
          reportColumns?.topLevelColumnsKey === 'date'
            ? params?.row[reportColumns?.dynamicColumns]?.filter(
                (item) => fMonthDayYear(item[reportColumns?.dynamicKey]) === col
              )
            : params?.row[reportColumns?.dynamicColumns]?.filter((item) => item[reportColumns?.dynamicKey] === col);
        return nameLookup?.map((item) => item?.[reportColumns?.dynamicKey]) || '';
      },
    })),
  ];
}

export async function getReportResultsMain(
  selectedReportId,
  dispatch,
  setOpenTimeoutSendAsEmail,
  formValues,
  download,
  sendAsEmail,
  afterLoad
) {
  if (selectedReportId === 0) return;
  dispatch(updateToMuchData(false));
  const res = await dispatch(
    downloadReportResults(selectedReportId, formValues, download, getColumnsMain(selectedReportId), sendAsEmail)
  );
  if (res === 'Timeout' && !sendAsEmail) {
    setOpenTimeoutSendAsEmail(true);
    dispatch(reportTimeout());
    return;
  }
  if (download && (formValues?.selectedReport?.downloadIsEmailed || sendAsEmail)) {
    if (sendAsEmail) setOpenTimeoutSendAsEmail(false);
    dispatch(updateReportResults({ message: 'email will be sent when ready' }));
  } else if (download && !res?.data?.size) enqueueSnackbar(`Report has no data`, { variant: 'error' });
  else if (download) {
    const headers = res.headers || {};
    const disposition = headers['content-disposition'] || '';
    const contentType = headers['content-type'] || '';

    const fileName =
      decodeURIComponent(disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i)?.[1] || '') ||
      `${formValues.selectedReport?.name ?? 'Report'}.${getExt(contentType)}`;

    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), {
      href: url,
      download: fileName,
    });
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
  }
  if (afterLoad) afterLoad();
}

export function checkForToMuchData(reportColumns, reportResults) {
  if ((!reportColumns?.dynamicColumns && !reportResults) || !Array.isArray(reportResults))
    return { reportColumns, reportResults };
  if (!reportColumns?.dynamicColumns && reportResults.length > 20000)
    return { reportColumns, reportResults: reportResults.slice(0, 20000), toMuchData: true };
  const allDynamicColumnsNames = [
    ...new Set(
      reportColumns.topLevelColumns?.map((col) => fMonthDayYear(col?.[reportColumns?.topLevelColumnsKey])) ??
      Array.isArray(reportResults)
        ? reportResults
            .filter((item) => item[reportColumns?.dynamicColumns])
            .flatMap((item) =>
              item[reportColumns?.dynamicColumns].map((subItem) => subItem?.[reportColumns?.dynamicKey])
            )
        : []
    ),
  ];
  const toMuchDynamicData =
    (allDynamicColumnsNames.length > 10 && reportResults.length > 500) ||
    (allDynamicColumnsNames.length > 20 && reportResults.length > 20);
  const toMuchData = toMuchDynamicData || reportResults.length > 20000;
  return {
    toMuchData,
    reportColumns,
    dynamicColumns: toMuchDynamicData ? allDynamicColumnsNames.slice(0, 10) : allDynamicColumnsNames,
    reportResults: toMuchDynamicData ? reportResults.slice(0, 500) : reportResults.slice(0, 20000),
  };
}

export function emptyFieldChecker(reportSpecificationOptions, formValues, selectedReportId, isScheduleReport) {
  const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
  const skipFields = [
    'Skip',
    'Take',
    'Companies',
    'Subcategories',
    'DateBetween',
    'ExcludeTicketData',
    'IncludeFacsWithoutGroup',
    'IncludeChildFacs',
    'PendingOnly',
    'PriceChangesOnly',
    'UseBudgetDate'
  ];
  const optionalFieldsByReport = {
    27: ['Facilities'],
  };
  const optionalFields = optionalFieldsByReport[selectedReportId] || [];
  return reportSpecificationOptions
    .map((opt) => {
      if (skipFields.includes(opt.name) || optionalFields.includes(opt.name)) return null;
      if (opt.isGenerateOnly && isScheduleReport) return null;
      if (opt.name === 'DateFilter')
        return (!!formValues.startDate && !!formValues.endDate) ||
          (!!formValues.dateValues && formValues.dateValues.length > 0)
          ? null
          : opt.name;
      if (opt.name === 'CustomDateRange')
        return isValidDate(formValues.startDate) && isValidDate(formValues.endDate) ? null : opt.name;
      if (opt.name === 'Month')
        return selectedReportId === 12 && isScheduleReport
          ? null
          : !!formValues.month && formValues.month.length > 0
          ? null
          : opt.name;
      if (opt.name === 'Year')
        return selectedReportId === 12 && isScheduleReport
          ? null
          : !!formValues.year && formValues.year.length > 0
          ? null
          : opt.name;
      if (opt.name === 'RentalVendor') return !!formValues.rentalVendor ? null : opt.name;
      const formValue = formValues[`${opt.name?.slice(0, 1)?.toLowerCase()}${opt.name?.slice(1)}`];
      return formValue && formValue.length > 0 ? null : opt.name;
    })
    .filter((name) => name);
}

export function getEmptyFields(reportSpecificationOptions, formValues, selectedReportId, isScheduleReport) {
  const missing = [];
  if (
    isScheduleReport &&
    !formValues?.timeFrame &&
    selectedReportId !== 12 &&
    selectedReportId !== 15 &&
    selectedReportId !== TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID
  )
    missing.push('Time frame');
  if (isScheduleReport && !formValues?.frequency) missing.push('Frequency');
  missing.push(...emptyFieldChecker(reportSpecificationOptions, formValues, selectedReportId, isScheduleReport));
  return missing;
}

export function getExt(type) {
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('csv')) return 'csv';
  if (type.includes('zip')) return 'zip';
  if (type.includes('excel') || type.includes('spreadsheet')) return 'xlsx';
  return 'dat';
}
